import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Check, Layers, Pencil, Plus, Trash2, X } from "lucide-react";
import { useToast } from "../../ui/Toast.js";
import { Field, TextInput } from "../../ui/forms.js";
import { Alert, Button, Card, CardHeader, ConfirmInline, IconButton, Skeleton } from "../../ui/primitives.js";
import type { TeamCollection, TeamRole } from "../../api/teams.js";
import { useCollectionMutations } from "../../data/teams.js";
import { formatRelativeTime } from "../../lib/format.js";
import { canManageTeam, describeTeamError } from "../../lib/teams.js";

interface CollectionsTabProps {
  readonly orgId: string;
  readonly role: TeamRole;
  readonly collections: ReadonlyArray<TeamCollection> | undefined;
  readonly pending: boolean;
}

export function TeamCollectionsTab(props: CollectionsTabProps): ReactNode {
  const manage = canManageTeam(props.role);
  const mutations = useCollectionMutations(props.orgId);
  const toast = useToast();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);

  function create(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed === "" || trimmed.length > 128) {
      setError("Bitte gib einen Namen mit höchstens 128 Zeichen ein.");
      return;
    }
    mutations.create.mutate(trimmed, {
      onSuccess: (): void => {
        setName("");
        setError(undefined);
      },
      onError: (err: Error): void => setError(describeTeamError(err))
    });
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader title="Sammlungen" description="Gruppen gemeinsamer Einträge, zum Beispiel Hosting, Buchhaltung oder Social Media." icon={Layers} />
      <div className="flex flex-col gap-5 p-5">
        {manage ? (
          <form onSubmit={create} className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end" noValidate>
            <Field id="collection-name" label="Neue Sammlung" error={error}>
              <TextInput
                id="collection-name"
                value={name}
                maxLength={128}
                autoComplete="off"
                placeholder="z. B. Hosting"
                onChange={(event): void => {
                  setName(event.target.value);
                  setError(undefined);
                }}
              />
            </Field>
            <Button type="submit" icon={Plus} loading={mutations.create.isPending}>
              Anlegen
            </Button>
          </form>
        ) : null}

        {props.pending ? <Skeleton className="h-32" /> : null}
        {props.collections !== undefined && props.collections.length === 0 ? (
          <p className="rounded-lg border border-dashed border-line-strong px-5 py-6 text-center text-sm text-mist">Noch keine Sammlungen.</p>
        ) : null}
        {mutations.remove.isError ? <Alert tone="danger">{describeTeamError(mutations.remove.error)}</Alert> : null}

        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-ink empty:hidden">
          {(props.collections ?? []).map((collection: TeamCollection) => (
            <CollectionRow
              key={collection.id}
              collection={collection}
              manage={manage}
              onRename={(newName: string): void => {
                mutations.rename.mutate(
                  { id: collection.id, name: newName },
                  { onError: (err: Error): void => toast.notify(describeTeamError(err), { tone: "danger" }) }
                );
              }}
              onDelete={(): void => {
                mutations.remove.mutate(collection.id, {
                  onSuccess: (): void => toast.notify("Sammlung gelöscht.", { tone: "success" }),
                  onError: (err: Error): void => toast.notify(describeTeamError(err), { tone: "danger" })
                });
              }}
              deleting={mutations.remove.isPending}
            />
          ))}
        </ul>
      </div>
    </Card>
  );
}

function CollectionRow(props: {
  readonly collection: TeamCollection;
  readonly manage: boolean;
  readonly onRename: (name: string) => void;
  readonly onDelete: () => void;
  readonly deleting: boolean;
}): ReactNode {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [draft, setDraft] = useState(props.collection.name);

  function save(): void {
    const trimmed = draft.trim();
    if (trimmed === "" || trimmed === props.collection.name) {
      setEditing(false);
      return;
    }
    props.onRename(trimmed);
    setEditing(false);
  }

  return (
    <li className="px-4 py-3">
      {editing ? (
        <div className="flex items-center gap-2">
          <TextInput
            aria-label="Neuer Name der Sammlung"
            value={draft}
            maxLength={128}
            autoFocus
            onChange={(event): void => setDraft(event.target.value)}
            onKeyDown={(event): void => {
              if (event.key === "Enter") {
                event.preventDefault();
                save();
              }
              if (event.key === "Escape") {
                setEditing(false);
              }
            }}
          />
          <IconButton label="Speichern" icon={Check} size="sm" onClick={save} />
          <IconButton label="Abbrechen" icon={X} size="sm" onClick={(): void => setEditing(false)} />
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid size-8 shrink-0 place-items-center rounded-lg border border-line bg-ink-3 text-flow">
              <Layers size={15} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foam">{props.collection.name}</p>
              <p className="text-[0.8125rem] text-mist-2">Erstellt {formatRelativeTime(props.collection.createdAt, Date.now())}</p>
            </div>
          </div>
          {props.manage ? (
            <div className="flex shrink-0 items-center gap-1">
              <IconButton
                label="Sammlung umbenennen"
                icon={Pencil}
                size="sm"
                onClick={(): void => {
                  setDraft(props.collection.name);
                  setEditing(true);
                }}
              />
              {confirming ? null : <IconButton label="Sammlung löschen" icon={Trash2} size="sm" tone="danger" onClick={(): void => setConfirming(true)} />}
            </div>
          ) : null}
        </div>
      )}
      {confirming ? (
        <div className="mt-3">
          <ConfirmInline
            message="Sammlung und alle darin enthaltenen Einträge löschen? Das lässt sich nicht rückgängig machen."
            confirmLabel="Endgültig löschen"
            pending={props.deleting}
            onConfirm={(): void => {
              setConfirming(false);
              props.onDelete();
            }}
            onCancel={(): void => setConfirming(false)}
          />
        </div>
      ) : null}
    </li>
  );
}
